import { z } from 'zod'
import type { core } from 'zod'

type Issue = core.$ZodRawIssue

/**
 * Plain-English text for Zod's built-in checks ("Required", "Use 64 characters
 * or fewer") instead of its defaults ("Too small: expected string to have
 * >=1 characters"). Messages a schema sets itself still win. Returns undefined
 * to fall back to Zod's default for anything not covered.
 */
export function humanZodMessage(issue: Issue): string | undefined {
  switch (issue.code) {
    case 'invalid_type':
      if (issue.input === undefined || issue.input === null) return 'Required'
      return undefined
    case 'too_small': {
      const min = Number(issue.minimum)
      if (issue.origin === 'string') {
        return min <= 1 ? 'Required' : `Use at least ${min} characters`
      }
      if (issue.origin === 'number') {
        return issue.inclusive ? `Must be ${min} or more` : `Must be more than ${min}`
      }
      if (issue.origin === 'array') {
        return min <= 1 ? 'Add at least one' : `Add at least ${min}`
      }
      return undefined
    }
    case 'too_big': {
      const max = Number(issue.maximum)
      if (issue.origin === 'string') return `Use ${max} characters or fewer`
      if (issue.origin === 'number') {
        return issue.inclusive ? `Must be ${max} or less` : `Must be less than ${max}`
      }
      if (issue.origin === 'array') return `Use ${max} or fewer`
      return undefined
    }
    case 'invalid_format':
      if (issue.format === 'email') return 'Enter a valid email address'
      if (issue.format === 'uuid') return 'Choose an option from the list'
      return undefined
    default:
      return undefined
  }
}

/** Install {@link humanZodMessage} for every schema. Call once at startup. */
export function installZodMessages(): void {
  z.config({ customError: humanZodMessage })
}
